
import { defineComponent, onBeforeMount, reactive } from 'vue'

// @components
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'

// @services
import locationAPI from '@/new-services/location.service'

// @validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { CarrierT } from '@/core/types/carriers.types'
import { carriers } from '@/core/realisations/carriers.realisation'

//constants
import { PHONE_NUMBER_MASK, PRICE_MASK } from '@/core/constants/masks.constants'
import { useMaska } from '@/common/composable/useMaska'

export default defineComponent({
    components: {
        NewModal,
        NewSelect,
        NewInput,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['create', 'cancel'],
    setup(props, { emit }) {
        const { unmaskNumbers } = useMaska()

        const state = reactive({
            is_loading: false,
        })

        const payload = reactive({
            companyId: null as number | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            loadAddress: null as string | null,
            receiverFullName: null as string | null,
            receiverPhoneNumber: null as string | null,
            dateOfLoad: null as string | null,
            unloadAddress: null as string | null,
            senderFullName: null as string | null,
            senderPhoneNumber: null as string | null,
            dateOfUnload: null as string | null,
            costOfTransportation: null as string | null,
            natureOfCargo: null as string | null,
            cubature: null as string | null,
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            carriers: {
                is_loading: false,
                is_failed: false,
                list: [] as CarrierT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
        })

        const TtnTMSValidation = {
            companyId: { required },
            fromCityId: { required },
            toCityId: { required },
            loadAddress: { required },
            receiverFullName: { required },
            receiverPhoneNumber: { required },
            dateOfLoad: { required },
            unloadAddress: { required },
            senderFullName: { required },
            senderPhoneNumber: { required },
            dateOfUnload: { required },
            costOfTransportation: { required },
            natureOfCargo: { required },
            cubature: { required },
        }

        const validation$ = useVuelidate(TtnTMSValidation, payload)

        //

        const getPayload = () => {
            return {
                ...payload,
                receiverPhoneNumber: unmaskNumbers(payload.receiverPhoneNumber as string),
                senderPhoneNumber: unmaskNumbers(payload.senderPhoneNumber as string),
                costOfTransportation: Number(unmaskNumbers(payload.costOfTransportation as string)),
            }
        }

        const emitCreate = () => {
            if (props.loading) return

            validation$.value.$touch()
            if (validation$.value.$invalid) return

            emit('create', getPayload())
        }

        const emitCancel = () => {
            if (props.loading) return
            emit('cancel')
        }

        // cities

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({
                    page,
                    search,
                    statusId: 2,
                    transportTypeId: 3,
                    isExtended: 0,
                })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        //

        onBeforeMount(() => {
            loadCities()
            loadCarriers(1)
        })

        //

        return {
            state,
            payload,
            data_state,
            //
            validation$,
            //
            loadCities,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            emitCancel,
            emitCreate,
            //
            PHONE_NUMBER_MASK,
            PRICE_MASK,
        }
    },
})
